/**
 * Badge - Pill-shaped status / category label with 8 chromatic colors, 3 sizes,
 * and an optional leading dot indicator.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type BadgeColor =
  | 'blue'
  | 'purple'
  | 'pink'
  | 'cyan'
  | 'green'
  | 'orange'
  | 'red'
  | 'yellow';

export type BadgeSize = 'small' | 'medium' | 'large';

export interface BadgeOptions extends BaseUIComponentOptions {
  /** Text displayed inside the badge. */
  label?: string;
  /** Color token. */
  color?: BadgeColor;
  /** Size preset. */
  size?: BadgeSize;
  /** Show a leading dot indicator. */
  dot?: boolean;
}

export class Badge extends BaseUIComponent<BadgeOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Badge',
    category: 'data',
    summary: 'Pill-shaped status / category label with 8 colors and 3 sizes.',
    useCases: ['status', 'tag', 'category', 'count chip', 'indicator', 'label'],
    optionsType: 'BadgeOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/badge.html',
      styles: '@engine/assets/ui/styles/badge.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Badge.metadata.assetPaths.template,
      stylesPath: Badge.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<BadgeOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      label: 'Badge',
      color: 'blue',
      size: 'medium',
      dot: false,
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      label: this.options.label,
      color: this.options.color,
      size: this.options.size,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-badge') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-badge-label]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.applyDot();
  }

  /** Update the badge label text. */
  public setLabel(label: string): void {
    this.options.label = label;
    if (this.labelElement) this.labelElement.textContent = label;
  }

  /** Swap the color token. */
  public setColor(color: BadgeColor): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`color-${this.options.color}`);
    this.options.color = color;
    this.rootElement.classList.add(`color-${color}`);
  }

  /** Swap the size preset. */
  public setSize(size: BadgeSize): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`size-${this.options.size}`);
    this.options.size = size;
    this.rootElement.classList.add(`size-${size}`);
  }

  /** Toggle the leading dot indicator. */
  public setDot(dot: boolean): void {
    this.options.dot = dot;
    this.applyDot();
  }

  private applyDot(): void {
    if (!this.rootElement) return;
    this.rootElement.dataset.dot = this.options.dot ? 'true' : 'false';
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.labelElement = null;
  }
}
